import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface CollapsiblePanelProps {
  /** Panel heading. A string is rendered as an h6; pass a node for custom markup. */
  title: React.ReactNode;
  /** Optional leading icon shown before the title. */
  icon?: React.ReactNode;
  /**
   * Right-aligned header content (captions, chips, buttons). Clicks/focus here do
   * NOT toggle the panel, so interactive controls are safe to place here.
   */
  actions?: React.ReactNode;
  /** DOM id on the accordion root (used for hash scrolling, e.g. #bank-account-balances). */
  id?: string;
  /** Start expanded (default true) so panels are visible on load but can be collapsed. */
  defaultExpanded?: boolean;
  variant?: 'elevation' | 'outlined';
  sx?: SxProps<Theme>;
  summarySx?: SxProps<Theme>;
  detailsSx?: SxProps<Theme>;
  titleVariant?: 'h6' | 'subtitle1';
  'data-testid'?: string;
  children: React.ReactNode;
}

/**
 * A titled dashboard panel that the user can collapse/expand. Wraps MUI Accordion
 * so every panel on the Dashboard behaves consistently (see RecurringTasksCard).
 */
const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title,
  icon,
  actions,
  id,
  defaultExpanded = true,
  variant = 'elevation',
  sx,
  summarySx,
  detailsSx,
  titleVariant = 'h6',
  children,
  ...rest
}) => {
  const testId = (rest as Record<string, unknown>)['data-testid'] as string | undefined;
  return (
    <Accordion
      id={id}
      data-testid={testId}
      defaultExpanded={defaultExpanded}
      disableGutters
      variant={variant}
      sx={{ '&:before': { display: 'none' }, ...sx }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1 }, ...summarySx }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
          {icon}
          {typeof title === 'string' ? <Typography variant={titleVariant}>{title}</Typography> : title}
          {actions ? (
            <Box
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              onClick={(event) => event.stopPropagation()}
              onFocus={(event) => event.stopPropagation()}
            >
              {actions}
            </Box>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default CollapsiblePanel;
